import { useEffect, useRef, useState } from 'react';

/**
 * Mirrors a boolean flag but, once it turns true, keeps it visible for at least
 * `minDurationMs` even if the underlying value flips back to false quickly.
 * Used so fast API responses still produce a perceptible loading indicator.
 */
export function useMinDurationFlag(active: boolean, minDurationMs: number = 400): boolean {
  const [visible, setVisible] = useState(active);
  const shownAtRef = useRef<number | null>(active ? Date.now() : null);

  useEffect(() => {
    if (active) {
      if (shownAtRef.current === null) {
        shownAtRef.current = Date.now();
      }
      setVisible(true);
      return;
    }

    const shownAt = shownAtRef.current;
    if (shownAt === null) {
      setVisible(false);
      return;
    }

    const remaining = minDurationMs - (Date.now() - shownAt);
    if (remaining <= 0) {
      shownAtRef.current = null;
      setVisible(false);
      return;
    }

    const timer = setTimeout(() => {
      shownAtRef.current = null;
      setVisible(false);
    }, remaining);
    return () => clearTimeout(timer);
  }, [active, minDurationMs]);

  return visible;
}
